import React, { useEffect, useState } from 'react';
import Dropdown from '../forecast/Dropdown';
import { FilledInput } from '../common/FilledInput';
import { Divider } from '../common/Divider';
import { OutlinedButton } from '../common/OutlinedButton';
import { useTranslation } from 'react-i18next';
import { generateSiteId } from '@/utils/util';

interface OperationManagementPowerPlantRegistrationModalSingleRegistrationProps {
  testId?: string;
  className?: string;
  isEdit?: boolean;
  data?: Partial<PowerPlantListTableData> | null;
}

export const OperationManagementPowerPlantRegistrationModalSingleRegistration: React.FC<
  OperationManagementPowerPlantRegistrationModalSingleRegistrationProps
> = ({ testId, className, data, isEdit }) => {
  const { t } = useTranslation();
  const [generatorNumber, setGeneratorNumber] = useState(
    parseInt(data?.generatorCount ?? '1') ?? 1
  );
  const [plantType, setPlantType] = useState(
    data?.siteClass ?? t('addAccountModal.defaultRole')
  );
  const [generatorType, setGeneratorType] = useState(
    Array.from({ length: generatorNumber }).map(
      (_, index) =>
        data?.plants?.[index]?.class ?? t('addAccountModal.defaultRole')
    )
  );

  const [plantName, setPlantName] = useState('');
  const [siteId, setSiteId] = useState(data?.siteId ?? '');

  useEffect(() => {
    setGeneratorType((prev) => {
      const newArray = [...prev];

      while (newArray.length < generatorNumber) {
        newArray.push(t('addAccountModal.defaultRole'));
      }

      return newArray.slice(0, generatorNumber);
    });
  }, [generatorNumber]);

  useEffect(() => {
    if (!isEdit) generateSiteId(plantName).then((value) => setSiteId(value));
  }, [plantName]);

  return (
    <div data-testid={testId} className={`${className}`}>
      <p className="noto-14-bold pt-[13px] leading-[23px] text-dark-3">
        {t('management.plantInformation')}
      </p>
      <section className="mt-[18px] flex items-center justify-between gap-4">
        <div>
          <p className="noto-medium-3 mb-[7px] leading-none text-dark-6">
            {t('management.plantType')}
          </p>
          <Dropdown
            name="plantType"
            className="w-[168px] text-dark-6 [&>div:first-child>button]:noto-normal-3 [&>div:last-child>p]:pop-normal-3 [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-light-2 [&>div:first-child>button]:py-[13px] [&>div:first-child>button]:pl-[9px] [&>div:first-child>button]:pr-[11px] [&>div:last-child]:ring-light-3"
            items={[t('management.solarPower'), t('management.windPower')]}
            selectedItem={plantType}
            onSelected={(text: string) => {
              setPlantType(text);
            }}
          />
        </div>
        <div>
          <p className="noto-medium-3 mb-[7px] leading-none text-dark-6">
            {t('management.plantName')}
          </p>
          <FilledInput
            text={data?.plantName}
            required={true}
            name="plantName"
            handleChange={(text) => setPlantName(text)}
            className="h-[36px] w-[168px]"
          />
        </div>
        <div>
          <p className="noto-medium-3 mb-[7px] leading-none text-dark-6">
            {t('management.energyMarketId')}
          </p>
          <FilledInput
            text={data?.kpxId}
            required={true}
            name="energyMarketId"
            className="h-[36px] w-[168px]"
          />
        </div>
        <div>
          <p className="noto-medium-3 mb-[7px] leading-none text-dark-6">
            {t('management.contactPerson')}
          </p>
          <FilledInput
            text={data?.contactPerson}
            required={true}
            name="contactPerson"
            className="h-[36px] w-[168px]"
          />
        </div>
      </section>
      <section className="mt-[15px] flex items-center justify-between gap-[15px]">
        <div>
          <p className="noto-medium-3 mb-[7px] leading-none text-dark-6">
            발전소id (site id)
          </p>
          <FilledInput
            required={true}
            value={siteId}
            readonly={true}
            text="자동생성된 코드 노출"
            name="siteId"
            className="h-[36px] w-[168px]"
          />
        </div>
        <div>
          <p className="noto-medium-3 mb-[7px] leading-none text-dark-6">
            {t('management.administrativeAddress')}
          </p>
          <FilledInput
            text={data?.location}
            required={true}
            name="administrativeAddress"
            className="h-[36px] w-[168px]"
          />
        </div>
        <div>
          <p className="noto-medium-3 mb-[7px] leading-none text-dark-6">
            {`${t('management.latitude')} (${t('management.latitudeRange')})`}
          </p>
          <FilledInput
            text={data?.coordinates?.split(',')[0].trim()}
            required={true}
            name="latitude"
            className="h-[36px] w-[168px]"
          />
        </div>
        <div>
          <p className="noto-medium-3 mb-[7px] leading-none text-dark-6">
            {`${t('management.longitude')} (${t('management.longitudeRange')})`}
          </p>
          <FilledInput
            text={data?.coordinates?.split(',')[1].trim()}
            required={true}
            name="longitude"
            className="h-[36px] w-[168px]"
          />
        </div>
      </section>
      <Divider className="mt-[21px] bg-[#BEC4CA]" />
      <p className="noto-14-bold pt-[13px] leading-[23px] text-dark-3">
        {t('management.generatorInformation')}
      </p>
      <div className="max-h-[250px] overflow-scroll">
        {Array.from({ length: generatorNumber }).map((_, index) => {
          return (
            <section
              key={index}
              className="mt-[18px] flex items-center justify-between gap-4"
            >
              <div>
                <p className="noto-medium-3 mb-[7px] leading-none text-dark-6">
                  {t('management.generatorId')}
                </p>
                <FilledInput
                  text={data?.plants?.[index]?.plantId}
                  required={true}
                  name="generatorId"
                  className="h-[36px] w-[168px]"
                />
              </div>
              <div>
                <p className="noto-medium-3 mb-[7px] leading-none text-dark-6">
                  {t('management.generatorName')}
                </p>
                <FilledInput
                  text={data?.plants?.[index]?.plantName}
                  required={true}
                  name="generatorName"
                  className="h-[36px] w-[168px]"
                />
              </div>
              <div>
                <p className="noto-medium-3 mb-[7px] leading-none text-dark-6">
                  {t('management.generatorCapacity', { context: 'short' })}
                </p>
                <FilledInput
                  text={data?.plants?.[index]?.capacity}
                  required={true}
                  name="generatorCapacity"
                  className="h-[36px] w-[168px]"
                />
              </div>
              <div>
                <p className="noto-medium-3 mb-[7px] leading-none text-dark-6">
                  {t('management.generatorType')}
                </p>
                <Dropdown
                  name="generatorType"
                  className="w-[168px] text-dark-6 [&>div:first-child>button]:noto-normal-3 [&>div:last-child>p]:pop-normal-3 [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-light-2 [&>div:first-child>button]:py-[13px] [&>div:first-child>button]:pl-[9px] [&>div:first-child>button]:pr-[11px] [&>div:last-child]:ring-light-3"
                  items={[
                    t('management.solarPower'),
                    t('management.windPower'),
                    'ESS',
                  ]}
                  selectedItem={generatorType[index]}
                  onSelected={(text: string) => {
                    setGeneratorType((prev) => {
                      const copy = [...prev];
                      copy[index] = text;
                      return copy;
                    });
                  }}
                />
              </div>
            </section>
          );
        })}
      </div>
      <OutlinedButton
        text={t('management.addGeneratorInformation')}
        isClicked={false}
        onClick={() => setGeneratorNumber((prev) => prev + 1)}
        className="mt-4 h-[36px] w-[120px] border-light-7 text-dark-5 [&]:noto-12-medium [&>p]:m-0 [&>p]:my-[6px] [&>p]:w-auto"
      />
    </div>
  );
};
